import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Users,
  MessageSquare,
  ArrowRight,
  Sparkles,
  Send,
  Eye,
  Reply,
} from "lucide-react";
import { format } from "date-fns";

const KPIS = [
  {
    key: "total_leads",
    label: "Total leads",
    icon: Users,
    from: "from-fun-blue",
    to: "to-fun-aqua",
    bar: "bg-fun-blue",
  },
  {
    key: "total_events",
    label: "Programmes",
    icon: Calendar,
    from: "from-fun-orange",
    to: "to-fun-yellow",
    bar: "bg-fun-orange",
  },
  {
    key: "unread_inbox",
    label: "Unread inbox",
    icon: MessageSquare,
    from: "from-fun-magenta",
    to: "to-fun-violet",
    bar: "bg-fun-magenta",
  },
];

// Fixed-order hue rotation for list-row avatars — same eight slots the KPI
// chips draw from, so a glance at any list feels like the same palette.
const ROW_HUES = [
  "bg-fun-blue",
  "bg-fun-orange",
  "bg-fun-aqua",
  "bg-fun-violet",
  "bg-fun-magenta",
  "bg-fun-green",
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  return (
    <div className="mx-auto max-w-7xl p-6 md:p-10" data-testid="dashboard-page">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
            <Sparkles className="h-3 w-3 text-fun-orange" />
            Home
          </div>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            Good to have you back.
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Lead-to-seat journey at a glance across every programme.
          </p>
        </div>
        <Badge className="animate-pulse bg-fun-green text-white hover:bg-fun-green">Live</Badge>
      </div>

      {/* KPIs */}
      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
        {KPIS.map(({ key, label, icon: Icon, from, to, bar }) => (
          <Card
            key={key}
            className="relative overflow-hidden border-border p-6 pt-7 shadow-sm transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg"
          >
            <div className={`absolute inset-x-0 top-0 h-1.5 ${bar}`} />
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                {label}
              </span>
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${from} ${to} text-white shadow-md`}
              >
                <Icon className="h-5 w-5" />
              </div>
            </div>
            <div className="mt-4 font-heading text-4xl font-extrabold" data-testid={`kpi-${key}`}>
              {data?.[key] ?? "—"}
            </div>
          </Card>
        ))}
      </div>

      {/* Two column: upcoming events + recent campaigns */}
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="overflow-hidden border-border border-l-4 border-l-fun-blue shadow-sm">
          <div className="flex items-center justify-between border-b border-border bg-fun-blue-tint px-6 py-4">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                Upcoming
              </div>
              <h3 className="mt-0.5 font-heading text-lg font-semibold">Programmes</h3>
            </div>
            <Link
              to="/events"
              className="inline-flex items-center gap-1 text-sm font-medium text-fun-blue hover:underline"
              data-testid="dashboard-events-link"
            >
              All events <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="divide-y divide-border">
            {(data?.upcoming_events || []).length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No programmes yet. Create your first event to get started.
              </div>
            )}
            {(data?.upcoming_events || []).map((ev, i) => (
              <div key={ev.id} className="flex items-center gap-3 p-4">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${ROW_HUES[i % ROW_HUES.length]}`}
                >
                  {ev.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{ev.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {safeDate(ev.date)} · {ev.venue || "TBD"}
                  </div>
                </div>
                <Link
                  to={`/events/${ev.id}/attendance`}
                  className="shrink-0 text-xs font-medium text-fun-blue hover:underline"
                >
                  Attendance →
                </Link>
              </div>
            ))}
          </div>
        </Card>

        <Card className="overflow-hidden border-border border-l-4 border-l-fun-magenta shadow-sm">
          <div className="flex items-center justify-between border-b border-border bg-fun-magenta-tint px-6 py-4">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                Recent
              </div>
              <h3 className="mt-0.5 font-heading text-lg font-semibold">Campaigns</h3>
            </div>
            <Link
              to="/campaigns"
              className="inline-flex items-center gap-1 text-sm font-medium text-fun-magenta hover:underline"
              data-testid="dashboard-campaigns-link"
            >
              All campaigns <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="divide-y divide-border">
            {(data?.recent_campaigns || []).length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No campaigns sent yet.
              </div>
            )}
            {(data?.recent_campaigns || []).map((c, i) => (
              <div key={c.id} className="flex items-center gap-3 p-4">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white ${ROW_HUES[(i + 2) % ROW_HUES.length]}`}
                >
                  <Send className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{c.template_name}</div>
                  <div className="truncate text-xs text-muted-foreground">{c.event_name}</div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <Stat icon={Send} label="Sent" value={c.sent} tone="text-fun-blue" />
                  <Stat icon={Eye} label="Read" value={c.read} tone="text-fun-violet" />
                  <Stat icon={Reply} label="Replied" value={c.replied} tone="text-fun-green" />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="flex flex-col items-center gap-0.5" title={label}>
      <Icon className={`h-3.5 w-3.5 ${tone}`} />
      <div className="font-heading text-sm font-bold">{value ?? 0}</div>
    </div>
  );
}

function safeDate(d) {
  try {
    return format(new Date(d), "PPP p");
  } catch {
    return d;
  }
}
